import { Component, DestroyRef, inject, Inject, Input, OnChanges, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Classifier, ClassifierService, CountryService, GlossaryId } from '@nano/common/classifier';
import {
  BeneficiaryIdentificationTypeOrganization,
  BeneficiaryIdentificationTypePrivate,
  usesStructuredPaymentFields,
  PaymentAdditionalField,
  PaymentConditions,
  PaymentConfig,
  PaymentCustomField,
  PaymentGatewayCustomParam,
  PaymentType
} from '@nano/payment/payment';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { distinctUntilChanged, Observable, Subject, takeUntil, tap } from 'rxjs';
import { IdentificationCodeType, LEI_PATTERN, PartyIdentificationType } from '@nano/common/party-identification';
import { getRegexMaxLength } from '@nano/common/util';
import {
  appliesGatewayDefault,
  GATEWAY_EDITABLE_DEFAULT_FIELDS,
  GATEWAY_FIELD_APPLICABILITY,
  isControlFilled,
  isHiddenBySelectedIdentification,
  isMirroredIdentificationClass,
  isShownForSelectedIdentification,
  PaymentFieldMaxLength
} from '../bank-transfer';
import { BeneficiaryNameSyncService } from '../beneficiary-name-sync.service';
import { PaymentIdentificationType } from '../payment-identification/payment-identification';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CONFIG } from '@nano/core/config';

interface GatewaySection {
  fields: string[];
  fieldsRenderedElsewhere?: string[];
  rendersCustomFields?: boolean;
  nestedSections?: string[];
  expandedWhenFilled?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-payment-gateway-info',
  templateUrl: './payment-gateway-info.component.html',
  providers: [BeneficiaryNameSyncService],
  standalone: false
})
export class PaymentGatewayInfoComponent implements OnInit, OnChanges {
  @Input() form: FormGroup;
  @Input() config: PaymentConfig;
  @Input() collapsed: boolean = false;
  @Input() beneficiaryCategory: AbstractControl;
  @Input() PaymentIdentificationClasses: string[] = [];
  @Input() shouldShowExchangeAmount: boolean = false;
  @Input() countries$: Observable<Classifier[]>;
  @Input() paymentConditions: PaymentConditions;
  @Input() gatewayAdditionalControlNames: string[];
  @Input() payerIdentificationTemplate?: TemplateRef<unknown>;
  @Input() initialPayerIdentificationTemplate?: TemplateRef<unknown>;
  @Input() beneficiaryIdentificationTemplate?: TemplateRef<unknown>;
  @Input() ultimateBeneficiaryIdentificationTemplate?: TemplateRef<unknown>;

  glossaryOptions: { [key: string]: Classifier[] } = {};
  additionalFields: PaymentAdditionalField[];
  customFields: PaymentCustomField[];
  sectionFields: { [section: string]: PaymentAdditionalField[] } = {};
  private manuallyToggledSections: { [section: string]: boolean } = {};
  private manuallyToggledSectionsOperationType?: PaymentType;

  private identificationOwnedFields = new Set<string>();

  private previousPrefilledFieldNames = new Set<string>();
  private prefilledFieldNames = new Set<string>();
  private processedFieldNames = new Set<string>();

  private readonly appliedDefaults = new Map<string, unknown>();

  private readonly dependencyDisabledFields = new Set<string>();

  readonly nameMaxLength = PaymentFieldMaxLength.beneficiaryIdentificationName;

  private readonly leiValidator: ValidatorFn = Validators.pattern(LEI_PATTERN);

  private static readonly identificationCodeControlNames: { [codeTypeControlName: string]: string } = {
    payerIdentificationCodeType: 'payerId',
    beneficiaryIdentificationCodeType: 'beneficiaryIdentificationCode',
    initialPayerIdentificationCodeType: 'initialPayerId',
    ultimateBeneficiaryIdentificationCodeType: 'ultimateBeneficiaryId'
  };

  private appliedBeneficiaryCategory?: string;

  private static readonly gatewaySections: { [section: string]: GatewaySection } = {
    beneficiaryType: {
      fields: [],
      fieldsRenderedElsewhere: [
        'beneficiaryCategory',
        'beneficiaryIdentification',
        'beneficiaryIdentificationType',
        'beneficiaryIdentificationCodeType',
        'beneficiaryIdentificationCode'
      ]
    },
    beneficiaryDetails: {
      fields: ['beneficiaryAccountType', 'beneficiaryNationality', 'beneficiaryRegistrationCountry', 'beneficiaryEmail'],
      fieldsRenderedElsewhere: ['beneficiaryPhoneNumber', 'beneficiaryIdentificationName', 'beneficiaryIdentificationSurname']
    },
    beneficiaryAddress: {
      fields: [
        'beneficiaryCountry',
        'beneficiaryProvince',
        'beneficiaryCity',
        'beneficiaryStreetName',
        'beneficiaryBuildingName',
        'beneficiaryBuildingNumber',
        'beneficiaryPostalCode',
        'beneficiaryAddress'
      ]
    },
    beneficiaryBank: { fields: ['additionalBeneficiaryBankCodeType', 'additionalBeneficiaryBankCode', 'accountInBeneficiaryBank'] },
    correspondentBank: {
      fields: [
        'correspondentBankCode',
        'correspondentBankName',
        'correspondentBankAddress',
        'correspondentBankCountry',
        'accountWithBankCorrespondent',
        'additionalBeneficiaryCorrespondentBankCodeType',
        'additionalBeneficiaryCorrespondentBankCode'
      ]
    },
    ultimateBeneficiary: {
      fields: ['ultimateBeneficiaryName', 'ultimateBeneficiaryIdentificationCodeType', 'ultimateBeneficiaryEmail'],
      fieldsRenderedElsewhere: ['ultimateBeneficiaryPhoneNumber'],
      nestedSections: ['ultimateBeneficiaryIdentification', 'ultimateBeneficiaryAddress']
    },
    ultimateBeneficiaryIdentification: {
      fields: [],
      fieldsRenderedElsewhere: [
        'ultimateBeneficiaryIdentification',
        'ultimateBeneficiaryIdentificationType',
        'ultimateBeneficiaryId',
        'ultimateBeneficiaryBirthDate',
        'ultimateBeneficiaryCountryOfBirth',
        'ultimateBeneficiaryProvinceOfBirth',
        'ultimateBeneficiaryCityOfBirth'
      ]
    },
    ultimateBeneficiaryAddress: {
      fields: [
        'ultimateBeneficiaryCountry',
        'ultimateBeneficiaryProvince',
        'ultimateBeneficiaryCity',
        'ultimateBeneficiaryStreetName',
        'ultimateBeneficiaryBuildingName',
        'ultimateBeneficiaryBuildingNumber',
        'ultimateBeneficiaryPostalCode'
      ]
    },
    payerAddress: { fields: ['payerCountry', 'payerProvince', 'payerCity', 'payerStreetName', 'payerBuildingNumber', 'payerPostalCode'] },
    payerIdentification: {
      expandedWhenFilled: 'payerIdentification',
      fields: [],
      fieldsRenderedElsewhere: [
        'payerIdentification',
        'payerIdentificationType',
        'payerBirthDate',
        'payerCountryOfBirth',
        'payerProvinceOfBirth',
        'payerCityOfBirth'
      ],
      nestedSections: ['payerIdentificationFallback']
    },
    payerIdentificationFallback: { fields: ['payerIdentificationCodeType', 'payerId'] },
    initialPayer: {
      fields: ['initialPayerName', 'initialPayerIdentificationCodeType', 'initialPayerEmail'],
      fieldsRenderedElsewhere: ['initialPayerPhoneNumber'],
      nestedSections: ['initialPayerIdentification', 'initialPayerAddress']
    },
    initialPayerIdentification: {
      fields: [],
      fieldsRenderedElsewhere: [
        'initialPayerIdentification',
        'initialPayerIdentificationType',
        'initialPayerId',
        'initialPayerBirthDate',
        'initialPayerCountryOfBirth',
        'initialPayerProvinceOfBirth',
        'initialPayerCityOfBirth'
      ]
    },
    initialPayerAddress: {
      fields: [
        'initialPayerCountry',
        'initialPayerProvince',
        'initialPayerCity',
        'initialPayerStreetName',
        'initialPayerBuildingName',
        'initialPayerBuildingNumber',
        'initialPayerPostalCode'
      ]
    },
    paymentIdentification: { fields: ['paymentIdentification', 'purposeCode'] },
    additionalInformation: { fields: ['sourceOfIncome'], rendersCustomFields: true }
  };

  readonly markRequiredFormFields = inject(CONFIG).markRequiredFormFields;

  private readonly fieldDependenciesTeardown$ = new Subject<void>();

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private classifierService: ClassifierService,
    private countryService: CountryService,
    private translationService: L10nTranslationService,
    private beneficiaryNameSync: BeneficiaryNameSyncService
  ) {}

  ngOnInit(): void {
    this.beneficiaryNameSync.connect({
      beneficiaryName: this.form?.get('beneficiaryName'),
      name: this.nameControl,
      surname: this.surnameControl,
      identification: this.beneficiaryCategory
    });

    this.appliedBeneficiaryCategory = undefined;
    if (this.beneficiaryCategory && this.nameControl && this.surnameControl) {
      this.applyBeneficiaryCategory(this.beneficiaryCategory.value as PartyIdentificationType);
    }

    this.beneficiaryCategory?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((value: PartyIdentificationType) => this.applyBeneficiaryCategory(value));
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.identificationOwnedFields = this.computeIdentificationOwnedFields();
    if (changes.paymentConditions) {
      this.loadAdditionalParameters();
    }
    if (this.beneficiaryCategory && this.nameControl && this.surnameControl) {
      this.applyBeneficiaryCategory(this.beneficiaryCategory.value as PartyIdentificationType);
    }
  }

  private applyBeneficiaryCategory(value: PartyIdentificationType): void {
    const availability = {
      name: this.controlNameExists('beneficiaryIdentificationName'),
      surname: this.controlNameExists('beneficiaryIdentificationSurname')
    };
    const applied = `${value}|${availability.name}|${availability.surname}`;

    if (this.appliedBeneficiaryCategory === applied) {
      this.beneficiaryNameSync.applyRenderSwitch(value, availability);
      return;
    }

    this.appliedBeneficiaryCategory = applied;
    this.beneficiaryNameSync.apply(value, availability);
  }

  private loadAdditionalParameters(): void {
    this.resetManuallyToggledSectionsOnOperationTypeChange();
    this.fieldDependenciesTeardown$.next();
    this.previousPrefilledFieldNames = this.prefilledFieldNames;
    this.prefilledFieldNames = new Set<string>();
    this.additionalFields = [...(this.paymentConditions?.additionalFields || [])];
    this.clearRequiredForDepartedFields(this.additionalFields);
    this.processFields(this.additionalFields, this.form);
    this.initFieldApplicability();
    this.computeSectionFields();
    this.initFieldDependencies();
    this.initBeneficiaryIdentificationCodePairing();
    this.initIdentificationCodeFormats();

    const previousCustomFieldLabels = this.getCustomFieldLabels(this.customFields ?? []);
    this.customFields = [...(this.paymentConditions?.customFields || [])];
    const customFieldsGroup = this.form.get('customFields') as FormGroup;
    this.processFields(this.customFields, customFieldsGroup);
    this.clearRoutingCodeValuesWhenLabelChanged(previousCustomFieldLabels, customFieldsGroup);
  }

  private clearRequiredForDepartedFields(fields: PaymentAdditionalField[]): void {
    const currentNames = new Set(fields.map((param) => param.name));

    this.processedFieldNames.forEach((name) => {
      if (currentNames.has(name)) {
        return;
      }
      const control = this.form.get(name);
      if (control?.hasValidator(Validators.required)) {
        control.removeValidators(Validators.required);
        control.updateValueAndValidity({ emitEvent: false });
      }
    });

    this.processedFieldNames = currentNames;
  }

  private resetManuallyToggledSectionsOnOperationTypeChange(): void {
    const operationType = this.paymentConditions?.operationType;
    if (operationType === this.manuallyToggledSectionsOperationType) {
      return;
    }

    this.manuallyToggledSectionsOperationType = operationType;
    this.manuallyToggledSections = {};
  }

  private identificationControlNames(): Set<string> {
    return new Set(
      this.additionalFields
        .flatMap((param) => {
          const rule = GATEWAY_FIELD_APPLICABILITY[param.name];
          return rule ? [rule.identificationControl, rule.identificationTypeControl] : [];
        })
        .filter((name): name is string => !!name)
    );
  }

  private initFieldApplicability(): void {
    const identificationControlNames = this.identificationControlNames();

    this.applyFieldApplicability();

    identificationControlNames.forEach((name) =>
      this.form
        .get(name)
        ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.fieldDependenciesTeardown$), untilDestroyed(this))
        .subscribe(() => this.applyFieldApplicability())
    );
  }

  private applyFieldApplicability(): void {
    this.additionalFields.forEach((param) => {
      if (!GATEWAY_FIELD_APPLICABILITY[param.name]) {
        return;
      }

      const control = this.form.get(param.name);
      if (!control) {
        return;
      }

      if (isHiddenBySelectedIdentification(param.name, this.form)) {
        this.prefilledFieldNames.delete(param.name);
        return;
      }

      const serverValue = this.appliedDefaults.get(param.name);
      if (serverValue === undefined || GATEWAY_EDITABLE_DEFAULT_FIELDS.includes(param.name)) {
        return;
      }

      if (!this.hasValue(control)) {
        control.setValue(serverValue, { emitEvent: false });
      }
      control.disable({ emitEvent: false });
      this.prefilledFieldNames.add(param.name);
    });
  }

  private initFieldDependencies(): void {
    const sources = this.additionalFields.filter((param) => param.requiredFields?.length || param.incompatibleFields?.length);
    if (!sources.length) {
      return;
    }

    const targetNames = new Set<string>();
    sources.forEach((param) => {
      param.requiredFields?.forEach((name) => targetNames.add(name));
      param.incompatibleFields?.forEach((name) => targetNames.add(name));
    });

    const isSourceActive = (param: PaymentAdditionalField): boolean =>
      isShownForSelectedIdentification(param.name, this.form) && this.hasValue(this.form.get(param.name));

    const requiresTarget = (param: PaymentAdditionalField, name: string): boolean =>
      isSourceActive(param) && !isMirroredIdentificationClass(param) && !!param.requiredFields?.includes(name);

    const recomputeDependencyState = (): void => {
      targetNames.forEach((name) =>
        this.applyIncompatibility(
          name,
          sources.some((param) => isSourceActive(param) && param.incompatibleFields?.includes(name))
        )
      );
      targetNames.forEach((name) =>
        this.applyDependencyRequired(name, isShownForSelectedIdentification(name, this.form) && sources.some((param) => requiresTarget(param, name)))
      );
    };

    recomputeDependencyState();

    const recomputeSourceNames = new Set([...sources.map((param) => param.name), ...this.identificationControlNames()]);
    recomputeSourceNames.forEach((name) => {
      this.form
        .get(name)
        ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.fieldDependenciesTeardown$), untilDestroyed(this))
        .subscribe(() => recomputeDependencyState());
    });
  }

  private applyDependencyRequired(name: string, requiredByActiveSource: boolean): void {
    const target = this.form.get(name);
    if (!target) {
      return;
    }

    const shouldBeRequired = requiredByActiveSource || this.isFieldMandatory(name);
    if (shouldBeRequired === target.hasValidator(Validators.required)) {
      return;
    }

    if (shouldBeRequired) {
      target.addValidators(Validators.required);
    } else {
      target.removeValidators(Validators.required);
    }
    target.updateValueAndValidity({ emitEvent: false });
  }

  private applyIncompatibility(name: string, blockedByActiveSource: boolean): void {
    const target = this.form.get(name);
    if (!target) {
      return;
    }

    if (blockedByActiveSource) {
      if (target.enabled) {
        target.reset(null, { emitEvent: false });
        target.disable({ emitEvent: false });
      }
      this.dependencyDisabledFields.add(name);
      return;
    }

    if (!this.dependencyDisabledFields.has(name)) {
      return;
    }
    this.dependencyDisabledFields.delete(name);

    const inCurrentFieldSet = this.additionalFields.some((field) => field.name === name);
    if (target.disabled && inCurrentFieldSet && !this.prefilledFieldNames.has(name)) {
      target.enable({ emitEvent: false });
    }
  }

  private hasValue(control: AbstractControl | null): boolean {
    const value: unknown = control?.value;
    return value !== null && value !== undefined && value !== '';
  }

  private isFieldMandatory(name: string): boolean {
    return !!this.additionalFields.find((field) => field.name === name)?.mandatory;
  }

  private computeSectionFields(): void {
    this.sectionFields = {};
    Object.entries(PaymentGatewayInfoComponent.gatewaySections).forEach(([section, definition]) => {
      this.sectionFields[section] = definition.fields
        .filter((name) => !this.isOwnedByIdentification(name))
        .map((name) => this.additionalFields.find((field) => field.name === name))
        .filter((field): field is PaymentAdditionalField => !!field && !!this.form.get(field.name));
    });
  }

  private get sectionsCollapsible(): boolean {
    return this.collapsed || usesStructuredPaymentFields(this.paymentConditions);
  }

  private sectionControlNames(section: string): string[] {
    const definition = PaymentGatewayInfoComponent.gatewaySections[section];
    if (!definition) {
      return [];
    }

    return [
      ...definition.fields,
      ...(definition.fieldsRenderedElsewhere || []),
      ...(definition.nestedSections || []).flatMap((nested) => this.sectionControlNames(nested))
    ];
  }

  private sectionCustomFields(section: string): PaymentCustomField[] {
    const definition = PaymentGatewayInfoComponent.gatewaySections[section];
    if (!definition) {
      return [];
    }

    return [
      ...(definition.rendersCustomFields ? (this.customFields ?? []) : []),
      ...(definition.nestedSections || []).flatMap((nested) => this.sectionCustomFields(nested))
    ];
  }

  private customFieldControl(name: string): AbstractControl | null {
    return this.form.get('customFields')?.get(name) ?? null;
  }

  isSectionExpanded(section: string): boolean {
    if (!this.sectionsCollapsible) {
      return true;
    }

    const controlNames = this.sectionControlNames(section);
    const customFields = this.sectionCustomFields(section);

    if (controlNames.some((name) => this.isControlRequired(name)) || customFields.some((field) => field.mandatory)) {
      return true;
    }

    if (
      controlNames.some((name) => this.hasVisibleValidationIssue(this.form.get(name))) ||
      customFields.some((field) => this.hasVisibleValidationIssue(this.customFieldControl(field.name)))
    ) {
      return true;
    }

    const manuallyToggled = this.manuallyToggledSections[section];
    if (manuallyToggled !== undefined) {
      return manuallyToggled;
    }

    const expandedWhenFilled = PaymentGatewayInfoComponent.gatewaySections[section]?.expandedWhenFilled;
    if (expandedWhenFilled && this.additionalFields?.some((field) => field.name === expandedWhenFilled)) {
      return this.hasValue(this.form.get(expandedWhenFilled));
    }

    return (
      controlNames.some((name) => this.hasVisibleValue(name, this.form.get(name))) ||
      customFields.some((field) => this.hasVisibleValue(field.name, this.customFieldControl(field.name)))
    );
  }

  get shouldShowAdditionalInformation(): boolean {
    return !!this.sectionFields.paymentIdentification?.length || !!this.sectionFields.additionalInformation?.length || !!this.customFields?.length;
  }

  get shouldShowPayerAddress(): boolean {
    return !!this.sectionFields.payerAddress?.length;
  }

  get shouldShowPayerIdentificationSection(): boolean {
    return this.shouldShowPayerIdentification || !!this.sectionFields.payerIdentificationFallback?.length;
  }

  shouldShowPhoneField(name: string): boolean {
    return !!this.form.get(name)?.enabled;
  }

  get shouldShowInitialPayer(): boolean {
    return (
      !!this.sectionFields.initialPayer?.length ||
      !!this.sectionFields.initialPayerAddress?.length ||
      this.shouldShowPhoneField('initialPayerPhoneNumber') ||
      this.shouldShowInitialPayerIdentification
    );
  }

  get shouldShowPayerInfo(): boolean {
    return this.shouldShowPayerAddress || this.shouldShowPayerIdentificationSection || this.shouldShowInitialPayer;
  }

  private isControlRequired(name: string): boolean {
    if (this.isFieldMandatory(name)) {
      return true;
    }

    const control = this.form.get(name);
    return !!control?.enabled && control.hasValidator(Validators.required);
  }

  private hasVisibleValidationIssue(control: AbstractControl | null): boolean {
    return !!control?.enabled && control.invalid && (control.touched || control.dirty || isControlFilled(control));
  }

  private hasVisibleValue(name: string, control: AbstractControl | null): boolean {
    return isControlFilled(control) || (this.appliedDefaults.has(name) && this.hasValue(control));
  }

  get isBeneficiaryTypeSectionForcedOpen(): boolean {
    return !!this.beneficiaryCategory?.enabled && !this.beneficiaryCategory.value;
  }

  onSectionActiveChange(section: string, active: boolean): void {
    if (!this.sectionsCollapsible) {
      return;
    }

    this.manuallyToggledSections[section] = active;
  }

  get shouldShowBeneficiaryIdentification(): boolean {
    return !!this.beneficiaryIdentificationTemplate && this.controlNameExists('beneficiaryIdentification', 'beneficiaryIdentificationType');
  }

  get shouldShowUltimateBeneficiaryIdentification(): boolean {
    return (
      !!this.ultimateBeneficiaryIdentificationTemplate && this.controlNameExists('ultimateBeneficiaryIdentification', 'ultimateBeneficiaryIdentificationType')
    );
  }

  get shouldShowPayerIdentification(): boolean {
    return !!this.payerIdentificationTemplate && this.controlNameExists('payerIdentification', 'payerIdentificationType');
  }

  get shouldShowInitialPayerIdentification(): boolean {
    return !!this.initialPayerIdentificationTemplate && this.controlNameExists('initialPayerIdentification', 'initialPayerIdentificationType');
  }

  isOwnedByIdentification(controlName: string): boolean {
    return this.identificationOwnedFields.has(controlName);
  }

  get shouldShowBeneficiaryIdentificationCode(): boolean {
    if (!this.shouldShowBeneficiaryIdentification) {
      return true;
    }
    return this.form.get('beneficiaryIdentificationType')?.value === PaymentIdentificationType.OTHER;
  }

  shouldShowInlineIdentificationField(controlName: string): boolean {
    if (this.isOwnedByIdentification(controlName)) {
      return false;
    }
    return controlName !== 'beneficiaryIdentificationCode' || this.shouldShowBeneficiaryIdentificationCode;
  }

  private initBeneficiaryIdentificationCodePairing(): void {
    const typeControl = this.form.get('beneficiaryIdentificationType');
    const codeTypeControl = this.form.get('beneficiaryIdentificationCodeType');
    const codeControl = this.form.get('beneficiaryIdentificationCode');
    if (!typeControl || !codeControl) {
      return;
    }

    const setRequired = (control: AbstractControl | null, required: boolean): void => {
      if (!control) {
        return;
      }
      const hasRequired = control.hasValidator(Validators.required);
      if (required === hasRequired) {
        return;
      }
      if (required) {
        control.addValidators(Validators.required);
      } else {
        control.removeValidators(Validators.required);
      }
      control.updateValueAndValidity({ emitEvent: false });
    };

    const applyPairing = (): void => {
      if (!this.shouldShowBeneficiaryIdentificationCode) {
        codeControl.reset(null, { emitEvent: false });
        codeControl.disable({ emitEvent: false });
        setRequired(codeControl, false);
        setRequired(codeTypeControl, false);
        return;
      }

      if (codeControl.disabled && !this.prefilledFieldNames.has('beneficiaryIdentificationCode')) {
        codeControl.enable({ emitEvent: false });
      }

      setRequired(codeControl, this.hasValue(codeTypeControl));
      setRequired(codeTypeControl, this.hasValue(codeControl));
    };

    applyPairing();
    [typeControl, codeTypeControl, codeControl]
      .filter(Boolean)
      .forEach((control) =>
        control.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.fieldDependenciesTeardown$), untilDestroyed(this)).subscribe(() => applyPairing())
      );
  }

  private initIdentificationCodeFormats(): void {
    Object.entries(PaymentGatewayInfoComponent.identificationCodeControlNames).forEach(([codeTypeName, codeName]) => {
      const codeTypeControl = this.form.get(codeTypeName);
      const codeControl = this.form.get(codeName);
      if (!codeTypeControl || !codeControl) {
        return;
      }

      this.applyIdentificationCodeTypeFormat(codeTypeControl, codeControl);

      codeTypeControl.valueChanges
        .pipe(distinctUntilChanged(), takeUntil(this.fieldDependenciesTeardown$), untilDestroyed(this))
        .subscribe(() => this.applyIdentificationCodeTypeFormat(codeTypeControl, codeControl));
    });
  }

  private applyIdentificationCodeTypeFormat(codeTypeControl: AbstractControl, codeControl: AbstractControl): void {
    codeControl.removeValidators(this.leiValidator);

    if (codeTypeControl.value === IdentificationCodeType.LEI) {
      codeControl.addValidators(this.leiValidator);
    }

    codeControl.updateValueAndValidity({ emitEvent: false });
  }

  private computeIdentificationOwnedFields(): Set<string> {
    const owned = new Set<string>();
    const claim = (prefix: string): void => {
      owned.add(`${prefix}IdentificationCodeType`);
      owned.add(`${prefix}Id`);
    };

    if (this.shouldShowBeneficiaryIdentification) claim('beneficiary');
    if (this.shouldShowUltimateBeneficiaryIdentification) claim('ultimateBeneficiary');
    if (this.shouldShowPayerIdentification) claim('payer');
    if (this.shouldShowInitialPayerIdentification) claim('initialPayer');

    return owned;
  }

  private clearRoutingCodeValuesWhenLabelChanged(previousLabels: Map<string, string>, customFieldsGroup: FormGroup): void {
    if (!previousLabels.size || !customFieldsGroup) {
      return;
    }

    this.customFields.forEach((field) => {
      if (!this.isRoutingCodeCustomField(field.name)) {
        return;
      }

      const fieldLabel = field.label;
      const previousLabel = previousLabels.get(field.name);
      if (previousLabel === undefined || previousLabel === fieldLabel) {
        return;
      }

      customFieldsGroup.get(field.name)?.setValue(null);
    });
  }

  routingCodeLabel(param: PaymentCustomField, language: string): string {
    const generic = 'payment.receiverInfo.routingCode.label';

    for (const candidate of [param?.label, param?.type]) {
      if (!candidate) {
        continue;
      }
      const key = `payment.receiverInfo.routingCode.${candidate}`;
      const translated = this.translationService.translate(key, undefined, language) as string;
      if (translated && translated !== key) {
        return translated;
      }
    }

    return this.translationService.translate(generic, undefined, language) as string;
  }

  private isRoutingCodeCustomField(fieldName: string): boolean {
    const routingCode1 = String(PaymentGatewayCustomParam.routingCode1);
    const routingCode2 = String(PaymentGatewayCustomParam.routingCode2);
    return fieldName === routingCode1 || fieldName === routingCode2;
  }

  private getCustomFieldLabels(fields: PaymentCustomField[]): Map<string, string> {
    const labels = new Map<string, string>();
    fields.forEach((field) => {
      labels.set(field.name, field.label);
    });
    return labels;
  }

  private processFields(fields: PaymentAdditionalField[], formGroup: FormGroup): void {
    fields.forEach((param) => {
      const control = formGroup.get(param.name);

      if (control) {
        control.clearValidators();
        this.applyMandatoryValidator(control, param.mandatory);
        this.applyMaxLengthFromParamOrStatic(param, control);
        this.applyRegexValidator(control, param.regex, param.glossaryId as GlossaryId, param);
        this.applyEmailValidator(param, control);
        this.handleGlossary(param, control, formGroup);
        control.updateValueAndValidity({ emitEvent: false });
        this.applyDefaultValue(param, control, fields);
      }
    });
  }

  private applyDefaultValue(param: PaymentAdditionalField, control: AbstractControl, fields: PaymentAdditionalField[]): void {
    const hasDefault = !(param.defaultValue === undefined || param.defaultValue === null || param.defaultValue === '') && appliesGatewayDefault(param, fields);

    if (!hasDefault) {
      if (this.appliedDefaults.has(param.name) && !this.userChangedDefault(param.name, control)) {
        control.reset(null, { emitEvent: false });
      }
      if (this.previousPrefilledFieldNames.has(param.name)) {
        control.enable({ emitEvent: false });
      }
      this.appliedDefaults.delete(param.name);
      return;
    }

    if (!this.userChangedDefault(param.name, control)) {
      control.setValue(param.defaultValue, { emitEvent: false });
      this.appliedDefaults.set(param.name, param.defaultValue);
    }

    if (GATEWAY_EDITABLE_DEFAULT_FIELDS.includes(param.name)) {
      return;
    }

    control.disable({ emitEvent: false });
    this.prefilledFieldNames.add(param.name);
  }

  private userChangedDefault(name: string, control: AbstractControl): boolean {
    if (!this.appliedDefaults.has(name)) {
      return false;
    }
    const current: unknown = control.value;
    if (current === null || current === undefined || current === '') {
      return false;
    }
    return current !== this.appliedDefaults.get(name);
  }

  private applyMaxLengthFromParamOrStatic(param: PaymentAdditionalField, control?: AbstractControl): void {
    param.maxlength = PaymentFieldMaxLength[param.name as keyof typeof PaymentFieldMaxLength];
    if (param.maxlength !== undefined) {
      control?.addValidators(Validators.maxLength(param.maxlength));
    }
  }

  private applyMandatoryValidator(control: AbstractControl, mandatory: boolean): void {
    if (mandatory) {
      control.addValidators([Validators.required]);
    } else {
      control.clearValidators();
    }
  }

  private applyEmailValidator(param: PaymentAdditionalField, control: AbstractControl): void {
    if (!this.isEmailField(param)) {
      return;
    }
    control.addValidators([Validators.email]);
    control.updateValueAndValidity();
  }

  private isEmailField(param?: PaymentAdditionalField): boolean {
    return !!param?.name.endsWith('Email');
  }

  private applyRegexValidator(control: AbstractControl, regex?: string, glossaryId?: GlossaryId, param?: PaymentAdditionalField): void {
    if (!regex || glossaryId || this.isEmailField(param)) return;

    const isRoutingCode = control.parent && control.parent.parent && control.parent.parent.get('customFields') === control.parent;
    if (isRoutingCode) {
      const fallbackMaxLength = param?.maxlength ?? PaymentFieldMaxLength.routingCode1;
      const regexMaxLength = getRegexMaxLength(regex);
      const maxLength = regexMaxLength === undefined ? fallbackMaxLength : Math.min(regexMaxLength, fallbackMaxLength);
      if (param) param.maxlength = maxLength;
      control.addValidators([Validators.pattern(new RegExp(regex, 'u')), Validators.maxLength(maxLength)]);
      control.updateValueAndValidity();
      return;
    }

    control.addValidators([Validators.pattern(new RegExp(regex, 'u'))]);
    const regexMaxLength = getRegexMaxLength(regex);
    if (regexMaxLength !== undefined) {
      const maxLength = param?.maxlength === undefined ? regexMaxLength : Math.min(regexMaxLength, param.maxlength);
      control.addValidators(Validators.maxLength(maxLength));
      if (param) param.maxlength = maxLength;
    }
    control.updateValueAndValidity();
  }

  private isIdentificationCodeTypeField(name: string): boolean {
    return name.endsWith('IdentificationCodeType');
  }

  private isBankCodeTypeField(name: string): boolean {
    return name.endsWith('BankCodeType');
  }

  private isCountryField(name: string): boolean {
    return name.endsWith('Country') || name.endsWith('Nationality');
  }

  private handleGlossary(param: PaymentAdditionalField, control: AbstractControl, formGroup: FormGroup): void {
    if (this.isIdentificationCodeTypeField(param.name)) {
      const exactCodeMatch = param.regex?.match(/^\^([A-Z0-9]+)\$$/);
      if (exactCodeMatch) {
        const value = exactCodeMatch[1];
        const isInPrivate = Object.values(BeneficiaryIdentificationTypePrivate).includes(value as BeneficiaryIdentificationTypePrivate);
        const isInOrganization = Object.values(BeneficiaryIdentificationTypeOrganization).includes(value as BeneficiaryIdentificationTypeOrganization);

        if (isInPrivate || isInOrganization) {
          control.setValue(value);
          this.glossaryOptions[param.name] = [
            {
              id: value,
              label: this.translationService.translate('payment.receiverInfo.beneficiaryIdentificationCodeType.classifier.' + value) as string
            }
          ];
          return;
        }
      }
      const allowedValues =
        this.beneficiaryCategory?.value === PartyIdentificationType.PRIVATE
          ? Object.values(BeneficiaryIdentificationTypePrivate)
          : Object.values(BeneficiaryIdentificationTypeOrganization);

      this.glossaryOptions[param.name] = allowedValues.map((value) => ({
        id: value as string,
        name: value as string,
        label: this.translationService.translate('payment.receiverInfo.beneficiaryIdentificationCodeType.classifier.' + value) as string
      }));
    } else if (this.isCountryField(param.name)) {
      this.countryService.worldwideCountries$
        .pipe(
          tap((countries) => {
            this.glossaryOptions[param.name] = countries;
          })
        )
        .subscribe();
    } else if (this.isBankCodeTypeField(param.name)) {
      this.classifierService.getGlossary((param.glossaryId as GlossaryId) || GlossaryId.SWIFT_XML_BANK_CODE_TYPE).subscribe({
        next: (options) => {
          this.glossaryOptions[param.name] = options.map((option) => ({
            ...option,
            label: option.label || option.id
          }));
          if (control.value && !this.glossaryOptions[param.name].some((option) => option.id === control.value)) {
            control.reset();
          }
        }
      });
    } else if (param.glossaryId) {
      this.classifierService.getGlossary(param.glossaryId as GlossaryId).subscribe({
        next: (options) => {
          this.glossaryOptions[param.name] = options.map((option) => ({
            ...option,
            label: option.label || option.id
          }));
          if (control.value && !this.glossaryOptions[param.name].some((option) => option.id === control.value)) {
            this.onGlossaryChange(param.name, 'OTHER');
            formGroup.get(`${param.name}_other`)?.setValue(control.value);
            control.setValue('OTHER');
          }
        }
      });
    }
  }

  diacriticInsensitiveFilter = (search: string, option: { nzLabel: string }): boolean => {
    const normalize = (str: string) =>
      (str || '')
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const normalizedOption = normalize(option.nzLabel);
    const normalizedSearch = normalize(search);
    return normalizedOption.includes(normalizedSearch);
  };

  onGlossaryChange(fieldName: string, value: string, strictValue?: boolean): void {
    if (!strictValue) {
      if (value === 'OTHER') {
        this.form.addControl(
          `${fieldName}_other`,
          new FormControl('', [
            Validators.required,
            ...(this.additionalFields.find((f) => f.name === fieldName)?.regex
              ? [Validators.pattern(new RegExp(this.additionalFields.find((f) => f.name === fieldName)?.regex, 'u'))]
              : [])
          ])
        );
      } else {
        const otherControl = this.form.get(`${fieldName}_other`);
        if (otherControl) {
          otherControl.reset();
          this.form.removeControl(`${fieldName}_other`);
        }
      }
    }
  }

  shouldShowDropdown(param: PaymentAdditionalField): boolean {
    return !!param.glossaryId || this.isIdentificationCodeTypeField(param.name) || this.isCountryField(param.name) || this.isBankCodeTypeField(param.name);
  }

  controlNameExists(...controlNames: string[]): boolean {
    return controlNames.some((controlName) => this.gatewayAdditionalControlNames.includes(controlName));
  }

  get nameControl(): AbstractControl {
    return this.form.get('beneficiaryIdentificationName');
  }

  get surnameControl(): AbstractControl {
    return this.form.get('beneficiaryIdentificationSurname');
  }

  get showNameControl(): boolean {
    return !!this.surnameControl?.enabled;
  }

  get nameControlLabelKey(): string {
    return this.showNameControl ? 'reg.details.personGivenName.label' : 'settings.profileSettings.profile-information.companyName';
  }

  isRequired(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control?.hasValidator?.(Validators.required) && this.markRequiredFormFields;
  }
}
